import { useEffect, useState } from "react";
import {
  Paper,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Chip,
  Snackbar,
  Alert,
} from "@mui/material";

/* 🔑 LocalStorage Key */
const STORAGE_KEY = "cpanel_users";

/* Initial Fallback Data */
const DUMMY_USERS = [
  {
    id: 1,
    name: "Ajinkya Rodge",
    email: "ajinkya@gmail.com",
    group: "Admin",
    status: "Active",
  },
  {
    id: 2,
    name: "Rahul Patil",
    email: "rahul@gmail.com",
    group: "Teacher",
    status: "Inactive",
  },
];

function User() {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");

  /* Modal */
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);

  /* Delete confirm */
  const [deleteId, setDeleteId] = useState(null);

  /* Snackbar */
  const [successMsg, setSuccessMsg] = useState("");

  /* Form */
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    group: "",
    status: "Active",
  });

  /* Load users */
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      setUsers(JSON.parse(stored));
    } else {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(DUMMY_USERS)
      );
      setUsers(DUMMY_USERS);
    }
  }, []);

  /* Search filter */
  const filtered = users.filter(
    (u) =>
      u.name.toLowerCase().includes(search.toLowerCase()) ||
      u.email.toLowerCase().includes(search.toLowerCase())
  );

  /* Open Add */
  const handleAdd = () => {
    setEditId(null);
    setFormData({
      name: "",
      email: "",
      group: "",
      status: "Active",
    });
    setOpen(true);
  };

  /* Open Edit */
  const handleEdit = (user) => {
    setEditId(user.id);
    setFormData(user);
    setOpen(true);
  };

  /* Save (Add / Update) */
  const handleSave = () => {
    if (!formData.name || !formData.email) return;

    const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
      formData.email
    );
    if (!emailValid) return;

    let updated = [];

    if (editId) {
      updated = users.map((u) =>
        u.id === editId ? { ...u, ...formData } : u
      );
      setSuccessMsg("User updated successfully");
    } else {
      updated = [
        ...users,
        { id: Date.now(), ...formData },
      ];
      setSuccessMsg("User added successfully");
    }

    setUsers(updated);
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify(updated)
    );
    setOpen(false);
  };

  /* Confirm Delete */
  const confirmDelete = () => {
    const updated = users.filter(
      (u) => u.id !== deleteId
    );
    setUsers(updated);
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify(updated)
    );
    setDeleteId(null);
    setSuccessMsg("User deleted successfully");
  };

  return (
    <Box>
      {/* Header */}
      <Typography variant="h4" fontWeight={700} mb={1}>
        User Management
      </Typography>
      <Typography color="text.secondary" mb={4}>
        Manage users and assign them to user groups
      </Typography>

      <Paper sx={{ p: 4, borderRadius: 3 }}>
        {/* Search + Add */}
        <Box
          display="flex"
          justifyContent="space-between"
          mb={3}
        >
          <TextField
            size="small"
            placeholder="Search user..."
            value={search}
            onChange={(e) =>
              setSearch(e.target.value)
            }
          />
          <Button variant="contained" onClick={handleAdd}>
            Add User
          </Button>
        </Box>

        {/* Table */}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: "#f8fafc" }}>
                <TableCell>S.No</TableCell>
                <TableCell>User Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>User Group</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">
                  Action
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {filtered.length > 0 ? (
                filtered.map((u, index) => (
                  <TableRow key={u.id} hover>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{u.name}</TableCell>
                    <TableCell>{u.email}</TableCell>
                    <TableCell>{u.group}</TableCell>
                    <TableCell>
                      <Chip
                        label={u.status}
                        color={
                          u.status === "Active"
                            ? "success"
                            : "default"
                        }
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        sx={{ mr: 1 }}
                        variant="outlined"
                        onClick={() =>
                          handleEdit(u)
                        }
                      >
                        Edit
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        variant="contained"
                        onClick={() =>
                          setDeleteId(u.id)
                        }
                      >
                        Delete
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    align="center"
                  >
                    <Typography
                      color="text.secondary"
                      py={4}
                    >
                      No users available
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Add / Edit Modal */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {editId ? "Edit User" : "Add User"}
        </DialogTitle>

        <DialogContent>
          <TextField
            label="User Name"
            fullWidth
            margin="normal"
            required
            value={formData.name}
            onChange={(e) =>
              setFormData({
                ...formData,
                name: e.target.value,
              })
            }
          />

          <TextField
            label="Email"
            fullWidth
            margin="normal"
            required
            value={formData.email}
            onChange={(e) =>
              setFormData({
                ...formData,
                email: e.target.value,
              })
            }
          />

          <TextField
            label="User Group"
            fullWidth
            margin="normal"
            value={formData.group}
            onChange={(e) =>
              setFormData({
                ...formData,
                group: e.target.value,
              })
            }
          />

          <TextField
            select
            label="Status"
            fullWidth
            margin="normal"
            value={formData.status}
            onChange={(e) =>
              setFormData({
                ...formData,
                status: e.target.value,
              })
            }
          >
            <MenuItem value="Active">
              Active
            </MenuItem>
            <MenuItem value="Inactive">
              Inactive
            </MenuItem>
          </TextField>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpen(false)} data-app-cancel="true">
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={
              !formData.name || !formData.email
            }
          >
            {editId ? "Update User" : "Save User"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm */}
      <Dialog
        open={Boolean(deleteId)}
        onClose={() => setDeleteId(null)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this user?
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteId(null)}
           data-app-cancel="true">
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={confirmDelete}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={Boolean(successMsg)}
        autoHideDuration={3000}
        onClose={() => setSuccessMsg("")}
      >
        <Alert severity="success" variant="filled">
          {successMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default User;
